<template>
  <div class="generator-container">
    <h2 class="gen-title">Create Custom Plan</h2>
    
    <form @submit.prevent="generate" class="gen-form">
        <!-- PLAN NAME -->
        <div class="form-group">
            <label>Plan Name</label>
            <input v-model="form.name" type="text" placeholder="e.g. My Gospels Study" required />
        </div>

        <!-- DURATION -->
        <div class="form-group">
            <label>Duration</label>
            <div class="duration-selection">
                <v-btn-toggle
                    v-model="selectedDuration"
                    color="primary"
                    variant="outlined"
                    mandatory
                    class="mb-3"
                >
                    <v-btn :value="90">90 Days</v-btn>
                    <v-btn :value="180">6 Months</v-btn>
                    <v-btn :value="365">1 Year</v-btn>
                    <v-btn :value="1095">3 Years</v-btn>
                    <v-btn value="custom">Custom</v-btn>
                </v-btn-toggle>

                <div v-if="selectedDuration === 'custom'" class="custom-input-wrapper">
                    <label class="sub-label">Enter number of days:</label>
                    <input 
                        type="number" 
                        v-model.number="form.durationDays" 
                        min="1" 
                        max="5000" 
                        class="custom-days-input"
                    />
                </div>
            </div>
        </div>

        <!-- SCOPE -->
        <div class="form-group">
            <label>Scope</label>
            <select v-model="form.scopeType">
                <option value="whole_bible">Whole Bible</option>
                <option value="ot">Old Testament Only</option>
                <option value="nt">New Testament Only</option>
                <optgroup label="Old Testament Collections">
                    <option value="pentateuch">Pentateuch (Law)</option>
                    <option value="history_ot">History (OT)</option>
                    <option value="wisdom">Wisdom & Poetry</option>
                    <option value="major_prophets">Major Prophets</option>
                    <option value="minor_prophets">Minor Prophets</option>
                </optgroup>
                <optgroup label="New Testament Collections">
                    <option value="gospels">Gospels</option>
                    <option value="history_nt">History (NT/Acts)</option>
                    <option value="pauline_epistles">Pauline Epistles</option>
                    <option value="general_epistles">General Epistles</option>
                </optgroup>
                <option value="custom_books">Select Books...</option>
            </select>
        </div>

        <!-- CUSTOM BOOKS SELECTOR -->
        <div v-if="form.scopeType === 'custom_books'" class="form-group sub-group">
            <label>Select Books</label>
            <div class="books-grid">
                <label v-for="book in allBooks" :key="book.name" class="book-checkbox">
                    <input type="checkbox" :value="book.name" v-model="form.selectedBooks" />
                    {{ book.name }}
                </label>
            </div>
        </div>

        <!-- OPTIONS ROW -->
        <div class="form-row">
            <div class="form-group half">
                <label>Schedule</label>
                <select v-model="form.scheduleType">
                    <option value="daily">Every Day</option>
                    <option value="weekdays">Weekdays Only (M-F)</option>
                </select>
            </div>
            <div class="form-group half">
                 <label>Order</label>
                 <select v-model="form.order">
                     <option value="canonical">Canonical (Gen -> Rev)</option>
                     <option value="chronological">Chronological (Historical)</option>
                 </select>
            </div>
            <div class="form-group half">
                 <label>Distribution</label>
                 <select v-model="form.loadBalance">
                     <option value="chapters">By Chapters (Standard)</option>
                     <option value="verses">By Verses (Balanced)</option>
                 </select>
            </div>
        </div>

        <button type="submit" class="submit-btn" :disabled="isGenerating">
            {{ isGenerating ? 'Generating...' : 'Generate Plan' }}
        </button>
    </form>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { useBibleStore } from '../stores/bible';
import { useRouter } from 'vue-router';

export default {
  name: 'CustomPlanGenerator',
  setup() {
    const bibleStore = useBibleStore();
    const router = useRouter();
    
    const isGenerating = ref(false);
    const selectedDuration = ref(365); // Default 1 Year

    const form = reactive({
        name: '',
        durationDays: 365,
        scopeType: 'whole_bible',
        selectedBooks: [],
        scheduleType: 'daily',
        order: 'canonical',
        loadBalance: 'chapters'
    });

    watch(selectedDuration, (val) => {
        if (val !== 'custom') {
            form.durationDays = val;
        }
    });

    const allBooks = computed(() => {
        return bibleStore.bibleData?.books || [];
    });

    const generate = async () => {
        isGenerating.value = true;
        
        try {
            // Build Definition
            const definition = {
                name: form.name,
                description: `Custom ${form.durationDays}-day plan.`,
                durationDays: form.durationDays,
                scope: {
                    type: form.scopeType,
                    bookNames: form.scopeType === 'custom_books' ? form.selectedBooks : undefined
                },
                schedule: { type: form.scheduleType },
                order: form.order,
                loadBalance: form.loadBalance,
                // Tags
                timeCommitment: 'variable', 
                focusTags: ['Custom'],
                paceTag: 'Custom'
            };

            // Generate for validation (optional, but good to check)
            // const plan = await planStore.generatePlanFromDefinition(definition, bibleStore.bibleData);
            
            // Encode Definition for URL (Base64)
            // Use UTF-8 safe encoding
            const json = JSON.stringify(definition);
            const base64 = btoa(unescape(encodeURIComponent(json)));

            // Navigate to the Base64 ID
            router.push(`/plan/${base64}`);
            
        } catch (e) {
            console.error("Generation failed", e);
            alert("Failed to generate plan. Please try again.");
        } finally {
            isGenerating.value = false;
        }
    };

    return {
        form,
        allBooks,
        generate,
        isGenerating,
        selectedDuration
    }
  }
}
</script>

<style scoped>
.generator-container {
    max-width: 800px;
    margin: 0 auto;
    background: #1a1c1c;
    padding: 40px;
    border-radius: 12px;
    border: 1px solid #333;
}

.gen-title {
    margin-bottom: 30px;
    text-align: center;
    color: #fff;
}

.form-group {
    margin-bottom: 24px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    color: #aaa;
    font-weight: 500;
}

input[type="text"], input[type="number"], select {
    width: 100%;
    padding: 12px;
    background: #0f1111;
    border: 1px solid #333;
    border-radius: 8px;
    color: #fff;
    font-size: 1rem;
}

.radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}

.radio-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    cursor: pointer;
}

.books-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
    max-height: 300px;
    overflow-y: auto;
    padding: 12px;
    background: #0f1111;
    border: 1px solid #333;
    border-radius: 8px;
}

.book-checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #ccc;
    font-size: 0.9rem;
    cursor: pointer;
}

.form-row {
    display: flex;
    gap: 24px;
}

.form-group.half {
    flex: 1;
}

.submit-btn {
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-accent)));
    color: #fff;
    font-weight: 700;
    font-size: 1.1rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 20px;
    transition: opacity 0.2s;
}

.submit-btn:hover {
    opacity: 0.9;
}
.submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.custom-input-wrapper {
    margin-top: 10px;
    animation: fadeIn 0.3s ease;
}

.sub-label {
    font-size: 0.9rem;
    color: #888;
    margin-bottom: 4px;
    display: block;
}

.custom-days-input {
    width: 200px !important;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 768px) {
    .form-row {
        flex-direction: column;
        gap: 16px;
    }

    .generator-container {
        padding: 24px 20px;
    }
    
    /* Force wrapping for duration toggle */
    :deep(.v-btn-group--density-default.v-btn-group) {
        flex-wrap: wrap;
        height: auto;
    }
    :deep(.v-btn-group .v-btn) {
        flex: 1 0 auto; /* Allow grow */
        min-width: 80px;
        height: 48px !important; /* Taller touch target */
    }
}
</style>
